import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { copy } from '../../config/copy';
import { Button, LinkButton } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { Artwork } from '../dashboard/Artwork';
import type { TrackSummary } from '../tracks/TrackList';
import { duration, rooms, type Room } from './api';

/**
 * Putting one of your tracks in a host's queue.
 *
 * ## Two steps, because they are two decisions
 *
 * Which track, then what to say about it. Collapsing them into one screen means
 * a note box sitting empty above a list somebody has not chosen from yet, and a
 * submit button that is disabled for a reason two hundred pixels away.
 *
 * Going back is free and keeps the note, because "actually, the other one"
 * happens constantly and losing a sentence somebody just wrote to it would be
 * the memorable part of the interaction.
 *
 * ## Only your own tracks
 *
 * The API accepts anybody's - that is how a room finds things, and the design's
 * queue is full of listeners submitting other people's records. This dialog is
 * the narrow case that needs no search: an artist putting their own work in
 * front of a host. Submitting somebody else's needs a catalogue search, which
 * is its own component and is not this one.
 */
export function SubmitDialog({
  room,
  username,
  onClose,
  onSubmitted,
}: {
  room: Room;
  /** Whose tracks to offer - the person doing the submitting. */
  username: string;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [chosen, setChosen] = useState<TrackSummary | null>(null);
  const [note, setNote] = useState('');
  const [useBoost, setUseBoost] = useState(false);
  const queryClient = useQueryClient();

  /*
   * What they have earned. Fetched here rather than carried on the session,
   * because it moves while somebody is sitting on the site - a host elsewhere
   * playing a track they submitted is what changes it.
   */
  const { data: boosts } = useQuery({
    queryKey: ['my-boosts'],
    queryFn: () => rooms.boosts(),
  });

  const { data, isPending } = useQuery({
    queryKey: ['tracks', username],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${username}/tracks`),
  });

  const submit = useMutation({
    mutationFn: () =>
      rooms.submit(room.username, chosen!.id, useBoost ? 'earned' : 'free', note.trim() || null),
    onSuccess: () => {
      /*
       * Spending one changes a number on screens this dialog cannot see - the
       * account menu carries it on every page, and the dashboard card carries
       * it too. Both read `my-boosts`, so one invalidation is the whole fix.
       *
       * Only when a boost was actually spent: a free submission changes no
       * balance, and refetching to confirm that would be a request per
       * submission that can only ever return what it already had.
       */
      if (useBoost) {
        void queryClient.invalidateQueries({ queryKey: ['my-boosts'] });
      }

      onSubmitted();
    },
  });

  /*
   * Only what a room can actually play. A track still encoding, or one the
   * artist kept private, would be refused by the server - offering it and then
   * failing is a worse way to explain the rule than not offering it.
   */
  const playable = (data?.items ?? []).filter(
    (track) => track.status === 'ready' && track.visibility === 'public',
  );

  return (
    <Modal
      title={chosen ? 'Anything for the host?' : `Submit to ${room.host.display_name}`}
      onClose={onClose}
    >
      {chosen ? (
        <div className="flex flex-col gap-5" data-submit-note>
          <Card className="flex items-center gap-3 p-3">
            <Artwork
              track={{ title: chosen.title, artwork: null, artwork_small: chosen.artwork_small ?? null }}
              className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-ink">{chosen.title}</span>
              <span className="font-mono text-[11px] text-faint">
                {duration(chosen.duration_ms)}
              </span>
            </div>
            <button
              type="button"
              data-change-track
              onClick={() => setChosen(null)}
              className="shrink-0 text-xs text-soft transition-colors hover:text-ink"
            >
              Change
            </button>
          </Card>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="submit-note" className="text-sm font-medium text-soft">
              A note, just for them
            </label>
            <textarea
              id="submit-note"
              value={note}
              maxLength={500}
              autoFocus
              data-note
              placeholder="First upload, the drop is at 1:40, no pressure — whatever helps them place it."
              onChange={(event) => setNote(event.target.value)}
              className="min-h-24 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
            />
            {/* Said plainly, because the whole value of the field is that it is
                not the public chat - and somebody who assumes otherwise will
                write something different. */}
            <p className="text-xs text-faint">
              Only {room.host.display_name} sees this. It is not shown in the room or the queue.
            </p>
          </div>

          {/*
            Offered only when they have one, and never as a default.
            It is spent, it does not come back, and a checkbox that starts
            ticked would spend it for people who never decided to.
          */}
          {(boosts?.available ?? 0) > 0 ? (
            <button
              type="button"
              role="switch"
              aria-checked={useBoost}
              data-use-boost
              onClick={() => setUseBoost((on) => !on)}
              className={`flex items-start gap-3 rounded-[var(--radius-card)] border p-3.5 text-left transition-colors ${
                useBoost
                  ? 'border-fresh-edge bg-fresh-wash'
                  : 'border-line hover:border-line-strong'
              }`}
            >
              <span
                aria-hidden
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
                  useBoost ? 'border-transparent bg-fresh text-app' : 'border-line-strong'
                }`}
              >
                {useBoost ? '✓' : ''}
              </span>

              <span className="flex flex-col gap-1">
                <span className={`text-sm font-medium ${useBoost ? 'text-fresh' : 'text-ink'}`}>
                  Spend a {copy['noun.boost.earned']}
                </span>
                <span className="text-xs leading-relaxed text-soft">
                  Goes in above every paid boost — only a host pick outranks it. You have{' '}
                  {boosts?.available === 1 ? '1' : boosts?.available}, and spending one does not get
                  it back.
                </span>
              </span>
            </button>
          ) : (
            // Said quietly, because it is the state almost everybody is in and
            // it should read as something to earn rather than something denied.
            boosts !== undefined && (
              <p className="font-mono text-[10px] leading-relaxed text-ghost">
                {copy['noun.boost.earned'].toLowerCase()} · {boosts.toward_next}/{boosts.threshold} — earned when hosts play the
                tracks you send them
              </p>
            )
          )}

          {submit.isError && (
            <Alert tone="error">
              {submit.error instanceof ApiError ? submit.error.message : 'That could not be sent.'}
            </Alert>
          )}

          <div className="flex items-center gap-3">
            <Button loading={submit.isPending} onClick={() => submit.mutate()} data-confirm-submit>
              Put it in the queue
            </Button>
            <Button variant="ghost" onClick={() => setChosen(null)} disabled={submit.isPending}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-submit-pick>
          <p className="text-sm text-soft">
            {room.status === 'live'
              ? 'It joins the queue and plays when it comes up.'
              : `${room.host.display_name} is off air. It will be waiting when they open.`}
          </p>

          {isPending ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : playable.length === 0 ? (
            <Card className="flex flex-col items-start gap-3 p-5">
              <p className="text-sm text-soft">
                {(data?.items.length ?? 0) === 0
                  ? 'You have not released anything yet.'
                  : 'Nothing of yours is public and finished processing yet.'}
              </p>
              <LinkButton to="/upload" variant="secondary">
                Drop a track
              </LinkButton>
            </Card>
          ) : (
            <ul className="flex max-h-[22rem] flex-col gap-2 overflow-y-auto" data-my-tracks>
              {playable.map((track) => (
                <li key={track.id}>
                  <button
                    type="button"
                    data-pick-track={track.id}
                    onClick={() => setChosen(track)}
                    className="flex w-full items-center gap-3 rounded-[var(--radius-card)] border border-line p-3 text-left transition-colors hover:border-line-strong hover:bg-raised"
                  >
                    <Artwork
                      track={{
                        title: track.title,
                        artwork: null,
                        artwork_small: track.artwork_small ?? null,
                      }}
                      className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-ink">{track.title}</span>
                      <span className="font-mono text-[11px] text-faint">
                        {duration(track.duration_ms)} · {track.play_count.toLocaleString()} plays
                      </span>
                    </span>
                    <span aria-hidden className="shrink-0 text-faint">
                      ›
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-faint">
            Submitting somebody else's track?{' '}
            <Link to="/search" className="text-accent hover:underline">
              Find it in search
            </Link>{' '}
            — a picker here is coming.
          </p>
        </div>
      )}
    </Modal>
  );
}
